import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { type ComponentProps } from 'react';
import { Link } from 'react-router';

import { PATHS, type NavinaPath } from '../../AppDomains';
import { theme } from '../../theme/theme';
import { ReactComponent as NavinaLogoIcon } from './Logo.svg';

export enum LogoSizes {
	Small = 'Small',
	Base = 'Base',
	Large = 'Large',
}

const LOGO_PIXEL_SIZES_MAP = {
	[LogoSizes.Small]: 27,
	[LogoSizes.Base]: 30,
	[LogoSizes.Large]: 40,
} as const satisfies Record<LogoSizes, number>;

export enum LogoDestination {
	Schedule = 'Schedule',
	QualityWorkspace = 'QualityWorkspace',
	Files = 'Files',
	Nowhere = 'Nowhere',
}

const LOGO_DESTINATIONS_MAP = {
	[LogoDestination.Schedule]: PATHS.SCHEDULE_PATH,
	[LogoDestination.QualityWorkspace]: PATHS.QUALITY_PATH,
	[LogoDestination.Files]: PATHS.FILES_PATH,
	[LogoDestination.Nowhere]: '',
} as const satisfies Record<LogoDestination, NavinaPath | ''>;

const LogoLinkContainer = styled(Link)`
	&,
	&:hover,
	&:focus,
	&:active {
		color: ${theme.colors.white};
	}

	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	block-size: var(--container-size);
	inline-size: var(--container-size);

	& > img,
	& > svg,
	& > picture {
		block-size: 100%;
		inline-size: 100%;
		object-fit: contain;
		object-position: center;
	}
`;

const LogoContainer = LogoLinkContainer.withComponent('div');

interface LogoProps extends Partial<ComponentProps<typeof LogoLinkContainer>> {
	readonly disableForwardToPage?: boolean;
	readonly size: LogoSizes;
	readonly destination: LogoDestination;
}

export const Logo = observer(function Logo({
	style,
	children = <NavinaLogoIcon />,
	destination,
	disableForwardToPage = false,
	...props
}: LogoProps) {
	const forwardToPage: boolean = !disableForwardToPage && destination !== LogoDestination.Nowhere;

	const Container = forwardToPage ? LogoLinkContainer : LogoContainer;

	return (
		<Container
			{...{
				to: LOGO_DESTINATIONS_MAP[destination],
				'aria-label': 'Move to the home page',
				style: {
					...style,
					'--container-size': `${LOGO_PIXEL_SIZES_MAP[props.size]}px`,
				},
				children,
				...props,
			}}
		/>
	);
});
